import React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Select, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const EMPTY_SENTINEL = "__cs_empty__";

/**
 * Drop-in replacement for native <select> using Radix primitives.
 *
 * Why bypass the shared SelectContent wrapper:
 *   The shadcn-shipped SelectContent ships with ChevronUp/Down scroll
 *   buttons + zoom/slide animations that cause a visible re-flow
 *   ("transición rara") on open as Radix measures content and decides
 *   whether the buttons are needed. Going direct to Radix with a plain
 *   overflow-scrollable viewport + fade-only animation makes the menu
 *   appear at its final size from frame 1 — same pattern as SuggestionSelect.
 *
 * @param {Object} props
 * @param {any} props.value
 * @param {Function} props.onChange
 * @param {string} [props.className]
 * @param {string} [props.contentClassName]
 * @param {React.ReactNode} [props.children]
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.required]
 * @param {boolean} [props.avoidCollisions=true] When true (default), the menu
 *   flips above the trigger if it would overflow below — keeps every option
 *   reachable for selects near the bottom of a modal. Pass false to force the
 *   menu to always open downward.
 */
export default function CustomSelect({
  value,
  onChange,
  className,
  contentClassName,
  children,
  placeholder,
  disabled,
  required,
  avoidCollisions = true,
  ..._props
}) {
  // Memo on children identity — without this, every render of the parent
  // walks all `<option>` children (which is ~240 for the country picker).
  // The parent's `lang`-keyed countryOptions cache + memo'd CountrySelect
  // already keep children stable, so this useMemo is a near-no-op cache hit
  // after the first render.
  const options = React.useMemo(
    () =>
      React.Children.toArray(children)
        .filter((child) => React.isValidElement(child) && child.type === "option")
        .map((child) => ({
          value:
            child.props.value === "" || child.props.value === undefined
              ? EMPTY_SENTINEL
              : String(child.props.value),
          label: child.props.children,
          disabled: child.props.disabled,
        })),
    [children]
  );

  const internalValue =
    value === "" || value === undefined || value === null ? EMPTY_SENTINEL : String(value);

  const handleValueChange = (val) => {
    if (onChange) {
      const outVal = val === EMPTY_SENTINEL ? "" : val;
      onChange({ target: { value: outVal } });
    }
  };

  const itemBaseCls =
    "relative flex w-full cursor-pointer select-none items-start rounded-lg py-2 pl-2 pr-3 text-sm outline-none " +
    "whitespace-normal break-words " +
    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50";

  return (
    <Select
      value={internalValue}
      onValueChange={handleValueChange}
      disabled={disabled}
      required={required}
    >
      <SelectTrigger
        className={cn(
          "bg-[#0f172a] border border-slate-700 text-white rounded-xl px-4 py-3 text-sm",
          "focus:border-[#0052FF] focus:ring-0 focus:outline-none h-auto",
          "data-[placeholder]:text-slate-400",
          className
        )}
      >
        <SelectValue placeholder={placeholder || " "} />
      </SelectTrigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={16}
          // avoidCollisions defaults to true (Radix default): when the menu
          // is taller than the room below the trigger — e.g. the category
          // picker sitting near the bottom of the New Expense modal — Radix
          // flips it above so every option stays reachable without scrolling
          // the whole modal. Callers that specifically want the always-open-
          // below behavior can pass avoidCollisions={false}.
          avoidCollisions={avoidCollisions}
          // Inline style bypasses Tailwind purging of the CSS var. Cap at 16rem
          // so a long menu (roles, currencies) always scrolls inside a tidy box
          // instead of running the full height available below the trigger;
          // still shrinks to the available height when there's less room.
          style={{ maxHeight: "min(16rem, var(--radix-select-content-available-height))" }}
          className={cn(
            "relative z-[100001]",
            "w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]",
            "overflow-y-auto overflow-x-hidden",
            "rounded-xl border border-slate-700 bg-[#0f172a] text-white shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            contentClassName
          )}
        >
          <SelectPrimitive.Viewport className="p-1 w-full">
            {options.map((opt, i) => (
              <SelectPrimitive.Item
                key={i}
                value={opt.value}
                disabled={opt.disabled}
                className={cn(itemBaseCls, "focus:bg-slate-700 data-[highlighted]:bg-slate-700")}
              >
                <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </Select>
  );
}
